/* rb-b-pants.js — one waist choice instead of two competing lists.

   CHANGES NOTHING IN SHOPIFY. It hides the theme's pant rows and drives their
   checkboxes; every value it applies is one the theme already rendered.

   THE PROBLEM
   The catalog stores pants two ways and the filter showed both as separate
   sections:
     Waist Sizes  — plain waist, e.g. 32 (111 products)
     Pant Sizes   — waist x inseam, e.g. 32x30, 32x32, 32x34, 32x36 (293 more)
   So a shopper picking 32 the obvious way saw 111. Choosing a waist here
   applies the plain value AND every wXi for that waist in one submit, which
   returns 272 — the true union. (Summing the facet counts suggests 404, but a
   pair stocked in both 32x30 and 32x32 is ONE product. Facet sums always
   overstate; only the live query is truthful.)

   ========== "WAIST SIZES" IS NOT ALL WAISTS ==========
   The theme buckets a size by its SHAPE, so any plain two-digit value lands
   under "Waist Sizes" — including sizes that are nothing of the kind:

     /collections/all      28,29,30,31,32,33,34,36,38,40,42,43,44,45
     /collections/footwear 42,43,44,45

   The first version trusted that bucket, so the waist dropdown offered 42-46
   on /all, and on FOOTWEAR it built a whole waist/inseam control out of
   nothing but shoe sizes.

   THE DISCRIMINATOR: in this catalog a genuine waist ALWAYS also exists as a
   wXi combo. The waist list is therefore built from the COMBOS, and a plain
   value is absorbed only when a combo shares its waist. 42-45 have no 42xNN,
   so they are never treated as waists. Verified against /collections/bottoms,
   which is clean (28-38 plain, all with combos) and behaves identically.

   ========== WHERE THE LEFTOVERS GO (replaces "Other sizes") ==========
   The first fix for the leftovers was a third heading called "Other sizes".
   Mark killed it: "i dont want other sizes just put them in pant sizes." He
   is right, and measuring the bucket showed the old heading was worse than a
   cosmetic wart — it was mislabelling REAL waists. What "Other sizes" held,
   per collection, as rendered:

     /collections/trunks    28,29,30,31,32,33,34,36,38   <- genuine waists
     /collections/jackets   40,42,44,46
     /collections/footwear  42,43,44,45
     /collections/all       42,43,44,45,46

   Trunks are sized by waist and stock no wXi combo, so the combo
   discriminator above classified every one of them a stray and the page
   announced nine real waist sizes as "Other sizes". That was a bug.

   Resolved against the catalog (what-are-strays.py), these values are:

     28-38  trunks, shorts, pants, denim, belts        -> waists
     40     5 Will Leather belts + 1 Taylor Stitch shirt -> a 40 belt IS a 40
     42-46  5 MOMA shoes (EU) + 1 Taylor Stitch shirt  -> not waists

   So the split is at 40, and it is not arbitrary: no belt, trouser or short
   in the catalog carries a plain value above 40, and nothing below 41 is a
   MOMA EU size.

     <= 40  moved under the collection's own "Pant Sizes" heading — or, where
            the collection has no pant bucket at all (trunks), that heading is
            simply renamed "Pant Sizes". This is what Mark asked for.
     >= 41  moved under "Shoe Sizes" and relabelled "EU 42", because they are
            MOMA EU sizes. Filing them under Pant Sizes would re-create the
            exact thing Mark flagged earlier — "so if i pick 42-45 shoes come
            up?" — by claiming a shoe has a 44 inch waist. Where a collection
            has no shoe bucket, they stay with the waists rather than vanish.

   They are NOT folded into the US chips. The catalog's own EU-to-US mapping
   contradicts itself — the shoe labels say EU 45 = US 12 AND EU 46 = US 12,
   while "13 (EU 46)" says EU 46 = US 13 — and one Taylor Stitch shirt shares
   42/44/46, so a fold would file a shirt under US 9. Relabelling is the most
   that is honest without touching data. Every value stays filterable; only
   its heading and its printed label change. The real fix is catalog-side and
   is deliberately NOT attempted here.

   "EU 42" also keeps rb-b-shoes.js off them for free: its usSize() only
   matches a bare number, so a relabelled row is skipped rather than sorted
   into the US run as an 8-13 chip.

   PLACEMENT: the control replaces the two native sections IN PLACE, inserted
   directly after the theme's own "Pant Sizes" subheading, which is what keeps
   the order Traditional -> Pant -> Shoe -> Misc.

   ONE PILL, NOT FIVE: applying a waist sends several values and the theme
   renders a pill per value. Those are hidden and replaced with a single
   'Waist 32' pill that clears the group in one click.

   EXCLUSIONS: 16x20 is not a pant (16 is not a waist we sell); inseam 33
   exists once, as 36x33, so it is not offered as a choice. */

(function () {
  'use strict';

  /* NO BREAKPOINT GATE since 10 Sep — this runs in the mobile drawer too.
     It was the last module still held back, because unlike the size-cleanup
     modules it BUILDS markup and therefore needs its CSS to come with it.
     rb-b-pants.css is now ungated as well, and Mark picked the layout: the
     existing two-dropdown control at full drawer width (option A of three
     mocked against the live drawer), over waist-as-chips or all-chips, both
     of which are far taller in a drawer that already scrolls.

     This also finally hides the raw wXi combos on mobile — the 30 rows of
     "32x34" that the drawer still lists on /collections/all — because this
     module owns that bucket. */

  var COMBO = /^(\d{2})\s*[xX]\s*(\d{2})$/;      // 32x34
  var PLAIN = /^(\d{2})$/;                        // 32
  var MIN_WAIST = 26, MAX_WAIST = 60;             // 16x20 is not a pant
  var MIN_INSEAM = 26, MAX_INSEAM = 40;
  var PANT_HEADING = 'Pant Sizes';
  var STRAY_WAIST_MAX = 40;   // see the note above: 40 is the last real waist

  /* EU -> US, taken from THIS catalog's own paired values rather than a
     generic conversion chart. Every one of these is asserted by a size value
     the store already sells:

       "9 (EU 42)"  22 products      "12 (EU 45)"  16 products
       "10 (EU 43)" 25 products      "12 (EU 46)"   8 products
       "11 (EU 44)" 25 products      "13 (EU 46)"  11 products
                                     "13 (EU 48)"   8 products

     42-45 are unanimous. 46 is NOT — the catalog calls it both US 12 and
     US 13 — so it is deliberately absent. (Nothing carries a plain 46 any
     more in any case: that was the Taylor Stitch Long Sleeve Shop Tee, which
     Mark re-sized to letters on 10 Sep.)

     THIS MODULE NO LONGER LABELS OR MOVES THEM. Mark chose the merge, so
     rb-b-shoes.js folds each of these into the US chip for the same shoe and
     they stop being separate chips at all. The map is kept here only as the
     skip list — placeStrays must leave these values alone, or it would drag
     them into Pant Sizes a frame before the fold happens. */
  var EU_TO_US = { 42: '9', 43: '10', 44: '11', 45: '12' };

  function q(s, r) { return (r || document).querySelector(s); }
  function all(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  function isSubhead(el) {
    return el && el.classList && el.classList.contains('collection-sidebar__size-subheader');
  }

  /* Read the pant values out of the rendered sidebar, so the control always
     reflects what this collection actually has. */
  function readModel(ul) {
    var model = { plain: {}, combos: {}, waists: {}, inseams: {}, rows: [],
                  stray: [], pantHead: null, waistHead: null, shoeHead: null };

    var bucket = null;
    var plainCandidates = [];

    Array.prototype.slice.call(ul.children).forEach(function (el) {
      if (isSubhead(el)) {
        var t = (el.textContent || '').trim().toLowerCase();
        if (t.indexOf('shoe') !== -1) { bucket = null; model.shoeHead = el; return; }
        /* A heading this module renamed to "Pant Sizes" on a previous pass
           reads as a pant bucket, which is correct and idempotent: its rows
           are re-tested against the combos and stay strays. The FIRST pant
           heading wins, so a rename can never displace the theme's own. */
        if (t.indexOf('pant') !== -1) {
          bucket = 'pant';
          if (!model.pantHead) model.pantHead = el;
        } else if (t.indexOf('waist') !== -1) {
          bucket = 'waist'; model.waistHead = el;
        } else bucket = null;
        return;
      }
      if (!bucket) return;

      var input = q('input', el);
      if (!input) return;
      var v = (input.value || '').trim();

      var m = v.match(COMBO);
      if (m) {
        var w = parseInt(m[1], 10), i = parseInt(m[2], 10);
        if (w < MIN_WAIST || w > MAX_WAIST || i < MIN_INSEAM || i > MAX_INSEAM) return;
        model.combos[v] = { w: String(w), i: String(i), input: input, row: el };
        (model.waists[w] = model.waists[w] || []).push(input);
        model.inseams[i] = (model.inseams[i] || 0) + 1;
        model.rows.push(el);
        return;
      }
      var p = v.match(PLAIN);
      if (p) {
        var pw = parseInt(p[1], 10);
        if (pw < MIN_WAIST || pw > MAX_WAIST) return;
        plainCandidates.push({ w: pw, input: input, row: el });
      }
    });

    /* A plain value is a waist only if a combo shares it. Anything else in
       this bucket is a two-digit size that is not a trouser waist. */
    plainCandidates.forEach(function (c) {
      if (model.waists[c.w]) {
        model.plain[c.w] = c.input;
        model.waists[c.w].push(c.input);
        model.rows.push(c.row);
      } else {
        model.stray.push({ row: c.row, w: c.w });
      }
    });

    return model;
  }

  /* Overwrite an element's first non-empty text node, leaving the count span
     and everything else in place. Same trick rb-b-shoes.js uses. */
  function setText(el, text) {
    var done = false;
    Array.prototype.forEach.call(el.childNodes, function (n) {
      if (n.nodeType === 3 && n.textContent.trim()) {
        n.textContent = done ? '' : text;
        done = true;
      }
    });
    if (!done) el.textContent = text;
  }

  /* Put every leftover under a heading that is true of it, and retire the
     "Other sizes" bucket. See the long note at the top for why the split is
     at 40 and why the EU sizes are relabelled rather than folded. */
  function placeStrays(ul, model) {
    if (!model.stray.length) return;

    /* EU-only shoe sizes belong to rb-b-shoes.js, which folds them into the
       US chips. Skipped here entirely — and skipped by VALUE, not by asking
       where the row currently sits, because on the re-render path this
       module runs BEFORE that one (110ms against 140ms) and would otherwise
       drag them into Pant Sizes a frame before they get folded. */
    var waistish = model.stray.filter(function (s) {
      return !EU_TO_US[s.w] && s.w <= STRAY_WAIST_MAX;
    });
    var shoeish = model.stray.filter(function (s) {
      return !EU_TO_US[s.w] && s.w > STRAY_WAIST_MAX;
    });

    /* Anything above the waist ceiling that rb-b-shoes does NOT own has no
       better home than the waist group — it is still a size somebody has to
       be able to click, and a value under no heading is a value nobody can
       find. Nothing hits this today; it exists so a new odd value shows up
       rather than disappearing. */
    waistish = waistish.concat(shoeish);
    if (!waistish.length) return;

    if (model.pantHead) {
      /* Move them under the theme's own pant heading. Reverse order because
         each is inserted directly after the heading; build() then puts the
         waist/inseam control above them by the same route. */
      waistish.sort(function (a, b) { return b.w - a.w; });
      waistish.forEach(function (s) {
        if (s.row.dataset.rbbPlaced === 'pant') return;
        s.row.dataset.rbbPlaced = 'pant';
        model.pantHead.parentNode.insertBefore(s.row, model.pantHead.nextSibling);
      });
    } else if (model.waistHead && model.waistHead.dataset.rbbRenamed !== '1') {
      /* No pant bucket at all — trunks. The values ARE waists, so the heading
         over them just gets the right name. */
      setText(model.waistHead, PANT_HEADING);
      model.waistHead.dataset.rbbRenamed = '1';
    }
  }

  function inputsForWaist(model, w) { return (model.waists[w] || []).slice(); }

  function inputsForCombo(model, w, i) {
    var e = model.combos[w + 'x' + i];
    return e ? [e.input] : [];
  }

  function inputsForInseam(model, i) {
    var out = [];
    Object.keys(model.combos).forEach(function (k) {
      if (model.combos[k].i === String(i)) out.push(model.combos[k].input);
    });
    return out;
  }

  function allPantInputs(model) {
    var out = [];
    Object.keys(model.waists).forEach(function (w) {
      model.waists[w].forEach(function (i) { out.push(i); });
    });
    return out;
  }

  function apply(model, w, i) {
    var wanted;
    if (w && i) wanted = inputsForCombo(model, w, i);
    else if (w) wanted = inputsForWaist(model, w);
    else if (i) wanted = inputsForInseam(model, i);
    else wanted = [];

    var everything = allPantInputs(model);
    everything.forEach(function (inp) { inp.checked = false; });
    wanted.forEach(function (inp) { inp.checked = true; });

    var fire = everything[0];
    if (fire) fire.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Read back from the checkboxes so the dropdowns survive the re-render. */
  function currentSelection(model) {
    var ws = {}, is = {}, n = 0;
    Object.keys(model.combos).forEach(function (k) {
      var e = model.combos[k];
      if (e.input.checked) { ws[e.w] = 1; is[e.i] = 1; n++; }
    });
    Object.keys(model.plain).forEach(function (w) {
      if (model.plain[w].checked) { ws[w] = 1; n++; }
    });
    if (!n) return { w: '', i: '' };
    var wk = Object.keys(ws), ik = Object.keys(is);
    return {
      w: wk.length === 1 ? wk[0] : '',
      i: (ik.length === 1 && n === 1) ? ik[0] : ''
    };
  }

  function build(ul, model) {
    if (q('.rbb-pants')) return;

    var li = document.createElement('li');
    li.className = 'rbb-pants-li';

    var box = document.createElement('div');
    box.className = 'rbb-pants';
    li.appendChild(box);

    var waists = Object.keys(model.waists).map(Number).sort(function (a, b) { return a - b; });
    var inseams = Object.keys(model.inseams).map(Number)
      .filter(function (i) { return model.inseams[i] > 1; })   // drops the lone 36x33
      .sort(function (a, b) { return a - b; });

    function select(label, values) {
      var wrap = document.createElement('label');
      wrap.className = 'rbb-pants__sel';
      var s = document.createElement('select');
      s.setAttribute('aria-label', label);
      var opt = document.createElement('option');
      opt.value = '';
      opt.textContent = label;
      s.appendChild(opt);
      values.forEach(function (v) {
        var o = document.createElement('option');
        o.value = String(v);
        o.textContent = String(v);
        s.appendChild(o);
      });
      wrap.appendChild(s);
      return { wrap: wrap, sel: s };
    }

    var W = select('Any waist', waists);
    var I = select('Any inseam', inseams);
    box.appendChild(W.wrap);
    box.appendChild(I.wrap);

    var cur = currentSelection(model);
    W.sel.value = cur.w;
    I.sel.value = cur.i;
    W.wrap.classList.toggle('is-set', !!cur.w);
    I.wrap.classList.toggle('is-set', !!cur.i);

    W.sel.addEventListener('change', function () { apply(model, W.sel.value, I.sel.value); });
    I.sel.addEventListener('change', function () { apply(model, W.sel.value, I.sel.value); });

    if (model.pantHead && model.pantHead.parentNode) {
      model.pantHead.parentNode.insertBefore(li, model.pantHead.nextSibling);
    } else {
      ul.appendChild(li);
    }
  }

  /* Hide a subheading with nothing visible left under it. placeStrays moves
     rows out from under the waist heading, so this is what actually retires
     it — checking model.stray would keep an empty heading on screen. */
  function hideEmptyHead(ul, head) {
    if (!head || !head.parentNode) return;
    var kids = Array.prototype.slice.call(ul.children);
    var i = kids.indexOf(head);
    if (i === -1) return;
    for (var j = i + 1; j < kids.length; j++) {
      if (isSubhead(kids[j])) break;
      if (getComputedStyle(kids[j]).display !== 'none') return;   // still in use
    }
    head.style.setProperty('display', 'none', 'important');
  }

  function hideNativeRows(model) {
    model.rows.forEach(function (el) {
      el.style.setProperty('display', 'none', 'important');
    });
  }

  /* The theme renders one pill per applied value. Collapse them to one. */
  function fixPills(model) {
    var bar = q('.collection__filters-active__wrapper');
    if (!bar) return;

    var pills = all('.collection__filters-active__filter:not(.collection__filters-active__filter--clear)', bar);
    var mine = pills.filter(function (a) {
      if (a.classList.contains('rbb-pantpill')) return false;
      var t = (a.textContent || '').trim();
      return COMBO.test(t) || (PLAIN.test(t) && model.plain[parseInt(t, 10)]);
    });

    var existing = q('.rbb-pantpill', bar);
    if (!mine.length) { if (existing) existing.remove(); return; }

    mine.forEach(function (a) { a.style.setProperty('display', 'none', 'important'); });

    var cur = currentSelection(model);
    var label = cur.w ? ('Waist ' + cur.w) : 'Pant size';
    if (cur.i) label += ' · Inseam ' + cur.i;

    if (existing) { existing.textContent = label; return; }

    var pill = document.createElement('a');
    pill.className = 'collection__filters-active__filter rbb-pantpill';
    pill.href = '#';
    pill.textContent = label;
    pill.addEventListener('click', function (ev) {
      ev.preventDefault();
      apply(model, '', '');
    });
    bar.insertBefore(pill, mine[0]);
  }

  function run() {
    var grp = q('.collection-sidebar__filter-group--size');
    if (!grp) return;
    var ul = q('.collection-sidebar__items', grp);
    if (!ul) return;

    var model = readModel(ul);

    /* Before anything else, and regardless of whether a control gets built:
       give every leftover a heading that is true of it. Footwear and trunks
       reach only these two lines. */
    placeStrays(ul, model);
    hideEmptyHead(ul, model.waistHead);

    /* No waist x inseam values means this is not a pants collection, whatever
       plain two-digit sizes happen to be sitting in the bucket. */
    if (!Object.keys(model.combos).length) return;
    if (!Object.keys(model.waists).length) return;

    hideNativeRows(model);
    hideEmptyHead(ul, model.waistHead);
    build(ul, model);
    fixPills(model);
  }

  function boot() {
    run();
    var section = q('.js-section__collection');
    if (!section || !window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(run, 110);
    }).observe(section, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
